package nl.staartvin.solarpower.data.util

import nl.staartvin.solarpower.data.model.UvPoint

/**
 * Linear interpolation of UVI at [millis] across a list of samples.
 *
 * Caller's contract: [samples] must be sorted by `time` ascending and
 * contain no duplicate timestamps. Returns `null` if [millis] is outside
 * the data range, so callers can decide whether to fall back.
 *
 * Used by:
 *  - the chart, to compute hour-marker heights and the scrub readout value
 *  - the repository, to compute the *current* UVI at the device's clock time
 *    (the API only returns hourly samples; the device's "now" sits between them).
 */
internal fun interpolateUvi(samples: List<UvPoint>, millis: Long): Double? {
    if (samples.isEmpty()) return null
    val firstMs = samples.first().time.toEpochMilliseconds()
    val lastMs = samples.last().time.toEpochMilliseconds()
    if (millis < firstMs || millis > lastMs) return null
    for (i in 0 until samples.size - 1) {
        val aMs = samples[i].time.toEpochMilliseconds()
        val bMs = samples[i + 1].time.toEpochMilliseconds()
        if (millis in aMs..bMs) {
            val total = (bMs - aMs).toDouble()
            val frac = if (total > 0) (millis - aMs).toDouble() / total else 0.0
            return samples[i].uvi + (samples[i + 1].uvi - samples[i].uvi) * frac
        }
    }
    return null
}
